import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { usePlatform } from '@/hooks/usePlatform';
import { useAppStore } from '@/store/appStore';
import { appAnalytics } from '@/services/analytics';

/**
 * RouteTracker component that tracks screen views for route changes
 */
export function RouteTracker() {
    const location = useLocation();
    const { appType } = usePlatform();
    // Signed in or not, not the user object: setUser replaces it (the cached
    // profile, then the fresh one), and each replacement logged the view again.
    const signedIn = useAppStore((s) => !!s.user);

    useEffect(() => {
        if (!signedIn) return;

        // Extract screen name from pathname
        const path = location.pathname;

        // Map routes to readable screen names
        let screenName = 'Unknown';

        // Skip base app paths — tab navigation is already tracked in App.tsx
        if (
            path === '/hallouminati' ||
            path === '/greekbrain' ||
            path === '/' ||
            path === '/login' ||
            path === '/auth/success'
        ) {
            return;
        }

        const searchParams = new URLSearchParams(location.search);

        if (path === '/hallouminati/geo-quiz') {
            screenName = 'Geo Practice Session';
        } else if (path === '/hallouminati/officials') {
            screenName = 'Officials List';
        } else if (path.includes('/words')) {
            screenName = 'Words List';
        } else if (path.includes('/struggling-words')) {
            screenName = 'Struggling Words';
        } else if (path.includes('/grammar/session') || /\/hallouminati\/practice(?:\?|$|\/)/.test(path)) {
            screenName = searchParams.get('category') === 'officials'
                ? 'Official Practice Session'
                : 'Practice Session';
        } else if (path.includes('/practice/speaking/picture')) {
            screenName = 'Speaking Picture';
        } else if (path.includes('/practice/speaking/dialogue')) {
            screenName = 'Tutor Dialogue';
        } else if (path.includes('/practice/speaking/topics')) {
            screenName = 'Speaking Topics';
        } else if (path.includes('/practice/speaking')) {
            screenName = 'Speaking';
        } else if (path.includes('/practice')) {
            screenName = 'Practice Hub';
        } else if (path.includes('/topic-mastery')) {
            const topic = path.split('/topic-mastery/')[1]?.split('/')[0];
            screenName = `Topic Mastery: ${topic || 'Unknown'}`;
        } else if (path.includes('/info/exam')) {
            screenName = 'About Exam';
        } else if (path.includes('/guide')) {
            // Logged elsewhere: the guide tab by MainApp, a chapter by
            // ChapterReader («View ChapterReader» with chapterId).
            return;
        } else if (path.includes('/quiz')) {
            const parts = path.split('/');
            const mode = parts[2] || 'unknown';
            const topic = parts[3];
            screenName = topic ? `Quiz: ${mode} - ${topic}` : `Quiz: ${mode}`;
        } else if (path.includes('/grammar/quiz')) {
            screenName = 'Tutor Quiz';
        } else if (path.includes('/grammar')) {
            screenName = 'Grammar';
        } else if (path.includes('/payment/success')) {
            screenName = 'Payment Success';
        } else if (path.includes('/legal/terms')) {
            screenName = 'Terms';
        } else if (path.includes('/legal/privacy')) {
            screenName = 'Privacy Policy';
        }

        // Only log if we identified the screen — skip unknown routes to avoid spam
        if (screenName !== 'Unknown') {
            appAnalytics.logEvent(`View ${screenName}`, {
                app_type: appType,
                path: path,
            });
        }
    }, [location.pathname, location.search, signedIn, appType]);

    return null;
}
